import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { canViewMerchants } from "../auth/roles";
import { clearToken } from "../auth/authService";
import { defaultPath } from "../routes";

/**
 * Sign-in screen for the platform-services user service.
 *
 * There was no login page before this: MSAL redirected the browser to Azure AD.
 */
export function Login() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const params = new URLSearchParams(location.search);
  const expired = params.get("reason") === "expired";
  const from = params.get("from") || defaultPath;

  useEffect(() => {
    //  Login renders no PageHeader, so it has to set its own title or the tab keeps
    //  whatever the previous page put there.
    document.title = "Log In · TowPay";
  }, []);

  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      setError("Enter your email address and password.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const { claims } = await login(email.trim(), password);

      //  A valid platform-services account is not the same as access to this app. Mirrors
      //  the role gate enterprise-team-tools-client applies at its own login.
      if (!canViewMerchants((claims && claims.roles) || [])) {
        clearToken();
        setError(
          "This account does not have access to Merchant Management. Contact Autura support."
        );
        return;
      }

      navigate(from, { replace: true });
    } catch (loginError) {
      //  authService has already reduced this to something safe to show.
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="row justify-content-center">
      <div className="col-12 col-md-6 col-lg-5">
        <div className="text-center mb-4">
          <img src="towpay-logo.png" className="logo" alt="TowPay Logo" />
          <h1 className="h4 mt-3">Merchant Management</h1>
        </div>

        {expired && (
          <div className="alert alert-info" role="alert">
            Your session expired. Log in again to continue.
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-3">
            <label htmlFor="email" className="form-label">
              Email:
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="password" className="form-label">
              Password:
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary w-100" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Signing In...
              </>
            ) : (
              "Log In"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
